import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import AppSidebar from './AppSidebar';
import BotaoTema from './BotaoTema';
import { useAuthStore, usePode, type AuthUser } from '@/stores/authStore';
import { useIndicesModal } from '@/stores/indicesModalStore';
import IndicesModal from '@/features/indices/IndicesModal';
import api from '@/lib/api';

/** Moldura das telas autenticadas: menu lateral, barra fina e o conteúdo. */
export default function AppShell() {
  const user = useAuthStore((estado) => estado.user);
  const podeVerIndices = usePode('indices.ver');
  const indicesAberto = useIndicesModal((estado) => estado.aberto);
  const fecharIndices = useIndicesModal((estado) => estado.fechar);

  // Permissão mudada pelo administrador vale sem sair e entrar (U-11): o /me é
  // buscado ao abrir o sistema e sempre que a pessoa volta para a janela — mas
  // não a cada foco: um diálogo de arquivo já dispara foco, e cada busca custa
  // ~1,5s no banco remoto. Um foco a menos de 30s do anterior é ignorado.
  const atualizarUsuario = useAuthStore((estado) => estado.atualizarUsuario);
  const ultimaBusca = useRef(0);
  useEffect(() => {
    const buscar = () => {
      ultimaBusca.current = Date.now();
      api.get<AuthUser>('/me').then((resposta) => atualizarUsuario(resposta.data)).catch(() => undefined);
    };
    buscar();

    const aoFocar = () => {
      if (Date.now() - ultimaBusca.current >= 30_000) {
        buscar();
      }
    };
    window.addEventListener('focus', aoFocar);

    return () => window.removeEventListener('focus', aoFocar);
  }, [atualizarUsuario]);

  // A altura da barra vira a variável --altura-barra: cabeçalhos de tabela que
  // grudam no topo ao rolar a página ficam logo abaixo dela, sem número mágico.
  const barra = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const elemento = barra.current;
    if (!elemento) return;

    const medir = () => document.documentElement.style.setProperty('--altura-barra', `${elemento.offsetHeight}px`);
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(elemento);

    return () => observador.disconnect();
  }, []);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div ref={barra} className="sticky top-0 z-40 bg-surface border-b border-border px-lg py-sm flex items-center gap-md">
          <SidebarTrigger />
          {user?.tenant && <span className="text-[13px] text-muted truncate">{user.tenant}</span>}

          {/* A conta (nome, tema e Sair) fica no rodapé do menu lateral. */}
          <div className="ml-auto flex items-center gap-xs">
            <BotaoTema />
          </div>
        </div>
        <Outlet />
      </SidebarInset>
      {podeVerIndices && <IndicesModal aberto={indicesAberto} aoFechar={fecharIndices} />}
    </SidebarProvider>
  );
}
